import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from '@/shared/hooks'
import { ArrowLeft, Printer, Loader2, AlertCircle } from 'lucide-react'
import { MarkdownPreview } from '@/shared/components'
import { useProjectByShortId, useTeam } from '../hooks'

export function SpecsPrintPage() {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { shortId } = useParams<{ shortId: string }>()

  const { data: project, isLoading: isLoadingProject, error: projectError } = useProjectByShortId(shortId)
  const { data: team, isLoading: isLoadingTeam } = useTeam(project?.team_id)

  const isLoading = isLoadingProject || isLoadingTeam

  const handlePrint = () => {
    window.print()
  }

  const handleBack = () => {
    navigate(`/p/${shortId}`)
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
          <span className="text-sm text-foreground-muted">{t('common.loading')}</span>
        </div>
      </div>
    )
  }

  // Error state
  if (projectError || !project) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <h1 className="text-lg font-semibold text-foreground">{t('common.error')}</h1>
          <p className="text-sm text-foreground-muted">{t('specs.print.notFound')}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
          >
            {t('common.backToDashboard')}
          </button>
        </div>
      </div>
    )
  }

  // No specs available
  if (!project.specs_content) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3 text-center">
          <AlertCircle className="h-12 w-12 text-amber-500" />
          <h1 className="text-lg font-semibold text-foreground">{t('specs.print.noSpecs')}</h1>
          <p className="text-sm text-foreground-muted">{t('specs.print.noSpecsDescription')}</p>
          <button
            onClick={handleBack}
            className="mt-4 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    )
  }

  const formattedDate = new Date().toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <div className="min-h-screen bg-white">
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden sticky top-0 z-10 flex items-center justify-between border-b border-border bg-white px-6 py-4">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-sm font-medium text-foreground-muted hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </button>
        <button
          onClick={handlePrint}
          className="flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
        >
          <Printer className="h-4 w-4" />
          {t('specs.print.printButton')}
        </button>
      </div>

      {/* Print Content */}
      <div className="mx-auto max-w-4xl px-8 py-12 print:max-w-none print:px-0 print:py-0">
        {/* Header */}
        <header className="mb-12 border-b border-border pb-8 print:mb-8 print:pb-6">
          <div className="mb-4 flex items-center gap-3">
            <img
              src="/heynicolai-logo.svg"
              alt="HeyNicolai"
              className="h-8 print:h-6"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <span className="text-lg font-semibold text-foreground print:text-base">HeyNicolai</span>
          </div>
          <h1 className="text-3xl font-bold text-foreground print:text-2xl">
            {project.name}
          </h1>
          <p className="mt-2 text-lg text-foreground-muted print:text-base">
            {t('specs.print.subtitle')}
          </p>
          <div className="mt-6 flex flex-wrap gap-6 text-sm text-foreground-muted print:mt-4 print:gap-4">
            {team && (
              <div>
                <span className="font-medium text-foreground">{t('specs.print.client')}:</span>{' '}
                {team.name}
              </div>
            )}
            <div>
              <span className="font-medium text-foreground">{t('specs.print.date')}:</span>{' '}
              {formattedDate}
            </div>
            {project.specs_updated_at && (
              <div>
                <span className="font-medium text-foreground">{t('specs.print.lastUpdated')}:</span>{' '}
                {new Date(project.specs_updated_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </header>

        {/* Specs Content */}
        <main className="prose prose-slate max-w-none print:prose-sm">
          <MarkdownPreview content={project.specs_content} />
        </main>

        {/* Footer - visible only in print */}
        <footer className="mt-16 hidden border-t border-border pt-6 text-center text-sm text-foreground-muted print:block print:mt-12 print:pt-4">
          <p>{t('specs.print.footer')}</p>
          <p className="mt-1">heynicolai.com</p>
        </footer>
      </div>
    </div>
  )
}
